import React, { useState, useEffect, useRef, useCallback } from 'react';
import AttachmentMenu from './AttachmentMenu';
import EmojiPicker from './EmojiPicker';
import MediaPicker from './MediaPicker';
import CameraCapture from './CameraCapture';
import DocumentPicker from './DocumentPicker';
import AudioPicker from './AudioPicker';
import ContactPicker from './ContactPicker';
import LocationPicker from './LocationPicker';
import VoiceRecorder from './VoiceRecorder';
import '../styles/MessageInput.css';

const SPEECH_LANGUAGE_LOCALES = {
  en: 'en-US',
  kn: 'kn-IN',
  hi: 'hi-IN',
  ta: 'ta-IN',
  te: 'te-IN',
  ml: 'ml-IN',
  mr: 'mr-IN',
  bn: 'bn-IN',
  gu: 'gu-IN',
  pa: 'pa-IN',
  ur: 'ur-IN',
};

const getSpeechLanguage = (configuredLanguage) => {
  if (configuredLanguage) return configuredLanguage;

  try {
    const savedSpeechLanguage = localStorage.getItem('chatapp-speech-language');
    if (savedSpeechLanguage) return savedSpeechLanguage;

    const appLanguage = localStorage.getItem('chatapp-language');
    if (appLanguage) {
      return SPEECH_LANGUAGE_LOCALES[appLanguage] || appLanguage;
    }
  } catch (_) {
    // localStorage can be unavailable in private or restricted browser contexts.
  }

  return 'en-US';
};

function MessageInput({ 
  text = '', 
  setText, 
  onSend, 
  onVoiceMessage,
  onMediaSend,
  onGifSend,
  onDocumentSend,
  onAudioSend,
  onContactSend,
  user,
  onLocationSend,
  replyTo = null, 
  onReplyClose,
  disabled = false,
  blockMessage = '',
  socket = null,
  to = null,
  selectedGroup = null,
  speechLanguage = null
}) {
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [showCameraCapture, setShowCameraCapture] = useState(false);
  const [showDocumentPicker, setShowDocumentPicker] = useState(false);
  const [showAudioPicker, setShowAudioPicker] = useState(false);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [isDocumentUploading, setIsDocumentUploading] = useState(false);
  const [isAudioUploading, setIsAudioUploading] = useState(false);
  const [isSpeechStarting, setIsSpeechStarting] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechError, setSpeechError] = useState('');
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);
  const recognitionRef = useRef(null);
  const silenceTimeoutRef = useRef(null);
  const speechBaseTextRef = useRef('');
  const finalTranscriptRef = useRef('');
  const receivedSpeechRef = useRef(false);
  const manualSpeechStopRef = useRef(false);
  const speechErrorRef = useRef(false);
  const speechStartRequestRef = useRef(0);

  const isDisabled = Boolean(disabled);
  const messageText = text || '';
  const blockMessageText = blockMessage || '';

  const clearSilenceTimeout = useCallback(() => {
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
  }, []);

  const stopSpeechRecognition = useCallback((manual = true) => {
    manualSpeechStopRef.current = manual;
    clearSilenceTimeout();

    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (_) {
        // Recognition may already have stopped.
      }
    }
    setIsListening(false);
  }, [clearSilenceTimeout]);

  const scheduleSilenceStop = useCallback(() => {
    clearSilenceTimeout();
    silenceTimeoutRef.current = setTimeout(() => {
      if (!receivedSpeechRef.current) {
        speechErrorRef.current = true;
        setSpeechError('No speech detected. Please try again.');
      }
      stopSpeechRecognition(false);
    }, 5000);
  }, [clearSilenceTimeout, stopSpeechRecognition]);

  // Show a reply preview for any message being replied to, including media
  // and call logs which have no text. Fall back to a type-based label.
  const getReplyPreviewText = (r) => {
    if (!r) return '';
    if (r.text && r.text.trim()) return r.text;
    switch (r.messageType || r.type) {
      case 'image':
        return '📷 Photo';
      case 'video':
        return '🎥 Video';
      case 'document':
        return '📄 Document';
      case 'audio':
        return '🎵 Audio';
      case 'voice':
        return '🎤 Voice message';
      case 'contact':
        return '👤 Contact';
      case 'location':
        return '📍 Location';
      case 'call':
        return '📞 Call';
      default:
        return '';
    }
  };

  const replyPreviewText = getReplyPreviewText(replyTo);
  const replyPreview = replyTo && replyPreviewText ? replyTo : null;
  const hasReplyTo = Boolean(replyPreview);

  // Typing indicator functionality
  const handleTyping = () => {
    if (!socket || !user || isDisabled) return;
    
    // Don't send typing events if no recipient
    if (!to && !selectedGroup) return;
    
    // If not already typing, emit typing start
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      if (selectedGroup) {
        socket.emit('groupUserTyping', {
          groupId: selectedGroup._id,
          userId: user.id,
          username: user.username,
          profileImage: user.profileImage,
          isTyping: true
        });
      } else if (to) {
        socket.emit('userTyping', {
          from: user.id,
          to: to,
          username: user.username,
          profileImage: user.profileImage,
          isTyping: true
        });
      }
    }
    
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set timeout to stop typing after 3 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      if (isTypingRef.current) {
        isTypingRef.current = false;
        if (selectedGroup) {
          socket.emit('groupUserTyping', {
            groupId: selectedGroup._id,
            userId: user.id,
            isTyping: false
          });
        } else if (to) {
          socket.emit('userTyping', {
            from: user.id,
            to: to,
            isTyping: false
          });
        }
      }
    }, 3000);
  };

  const stopTyping = () => {
    if (!socket || !user || !isTypingRef.current) return;
    
    isTypingRef.current = false;
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    
    if (selectedGroup) {
      socket.emit('groupUserTyping', {
        groupId: selectedGroup._id,
        userId: user.id,
        isTyping: false
      });
    } else if (to) {
      socket.emit('userTyping', {
        from: user.id,
        to: to,
        isTyping: false
      });
    }
  };

  // Cleanup typing on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      stopTyping();
    };
  }, []);

  // Stop typing when switching chats
  useEffect(() => {
    stopTyping();
    speechStartRequestRef.current += 1;
    stopSpeechRecognition();
    setIsSpeechStarting(false);
  }, [to, selectedGroup, stopSpeechRecognition]);

  const handleSend = () => {
    if (!messageText.trim() || isDisabled || !hasSend) return;
    stopTyping();
    onSend();
  };

  const handleAttachmentClick = () => {
    setShowAttachmentMenu(!showAttachmentMenu);
    setShowEmojiPicker(false);
  };

  const handleEmojiClick = () => {
    setShowEmojiPicker(!showEmojiPicker);
    setShowAttachmentMenu(false);
  };

  const handleAttachmentOption = (option) => {
    // Handle different attachment options
    switch (option) {
      case 'photos':
        if (hasMediaSend) {
          setShowMediaPicker(true);
          setShowAttachmentMenu(false);
        }
        break;
      case 'camera':
        if (hasMediaSend) {
          setShowCameraCapture(true);
          setShowAttachmentMenu(false);
        }
        break;
      case 'document':
        if (hasDocumentSend) {
          setShowDocumentPicker(true);
          setShowAttachmentMenu(false);
        }
        break;
      case 'contact':
        if (onContactSend) {
          setShowContactPicker(true);
          setShowAttachmentMenu(false);
        }
        break;
      case 'location':
        if (hasLocationSend) {
          setShowLocationPicker(true);
          setShowAttachmentMenu(false);
        }
        break;
      case 'audio':
        if (hasAudioSend) {
          setShowAudioPicker(true);
          setShowAttachmentMenu(false);
        }
        break;
      default:
        break;
    }
  };

  const handleEmojiSelect = (emoji) => {
    if (hasSetText) {
      setText(messageText + emoji);
    }
  };

  const handleGifSelect = (gifUrl, gifName) => {
    // Send GIF URL immediately via socket — no download/re-upload delay
    if (onGifSend) {
      onGifSend(gifUrl, gifName);
      return;
    }

    // Fallback for older callers: fire-and-forget download + media upload
    if (onMediaSend) {
      (async () => {
        try {
          const response = await fetch(gifUrl);
          const blob = await response.blob();
          const gifFile = new File([blob], `${gifName || 'gif'}.gif`, {
            type: 'image/gif',
          });
          onMediaSend([gifFile]);
        } catch (error) {
          console.error('Error fetching GIF:', error);
          if (hasSetText) {
            setText(messageText + gifUrl);
          }
        }
      })();
      return;
    }

    if (hasSetText) {
      setText(messageText + gifUrl);
    }
  };

  const closeAttachmentMenu = () => {
    setShowAttachmentMenu(false);
  };

  const closeEmojiPicker = () => {
    setShowEmojiPicker(false);
  };

  const handleMediaSelect = (mediaFiles, options) => {
    if (onMediaSend) {
      onMediaSend(mediaFiles, options);
    }
    setShowMediaPicker(false);
  };

  const closeMediaPicker = () => {
    setShowMediaPicker(false);
  };

  const handleCameraCapture = (photoFiles, options) => {
    if (onMediaSend) {
      onMediaSend(photoFiles, options);
    }
    setShowCameraCapture(false);
  };

  const closeCameraCapture = () => {
    setShowCameraCapture(false);
  };

  const handleDocumentSelect = async (documentFiles) => {
    if (onDocumentSend) {
      setIsDocumentUploading(true);
      try {
        await onDocumentSend(documentFiles);
      } catch (error) {
        console.error('Document upload failed:', error);
      } finally {
        setIsDocumentUploading(false);
        setShowDocumentPicker(false);
      }
    } else {
      setShowDocumentPicker(false);
    }
  };

  const closeDocumentPicker = () => {
    setShowDocumentPicker(false);
  };

  const handleAudioSelect = async (audioFiles, options) => {
    if (onAudioSend) {
      setIsAudioUploading(true);
      try {
        await onAudioSend(audioFiles, options);
      } catch (error) {
        console.error('Audio upload failed:', error);
      } finally {
        setIsAudioUploading(false);
        setShowAudioPicker(false);
      }
    } else {
      setShowAudioPicker(false);
    }
  };

  const closeAudioPicker = () => {
    setShowAudioPicker(false);
  };

  const handleContactSelect = async (contact) => {
    if (onContactSend) {
      try {
        await onContactSend(contact);
      } catch (error) {
        console.error('Contact send failed:', error);
      } finally {
        setShowContactPicker(false);
      }
    } else {
      setShowContactPicker(false);
    }
  };

  const closeContactPicker = () => {
    setShowContactPicker(false);
  };

  const handleLocationSelect = async (locationData) => {
    if (onLocationSend) {
      try {
        await onLocationSend(locationData);
      } catch (error) {
        console.error('Location send failed:', error);
      } finally {
        setShowLocationPicker(false);
      }
    } else {
      setShowLocationPicker(false);
    }
  };

  const closeLocationPicker = () => {
    setShowLocationPicker(false);
  };

  const hasVoiceMessage = Boolean(onVoiceMessage);
  const hasMediaSend = Boolean(onMediaSend);
  const hasDocumentSend = Boolean(onDocumentSend);
  const hasAudioSend = Boolean(onAudioSend);
  const hasContactSend = Boolean(onContactSend);
  const hasLocationSend = Boolean(onLocationSend);
  const hasSend = Boolean(onSend);
  const hasSetText = Boolean(setText);
  const hasReplyClose = Boolean(onReplyClose);
  const hasBlockMessage = Boolean(blockMessageText);

  const startSpeechRecognition = async () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setSpeechError('Speech recognition is unavailable in this browser.');
      return;
    }
    if (!hasSetText || isDisabled || isSpeechStarting) return;

    const requestId = speechStartRequestRef.current + 1;
    speechStartRequestRef.current = requestId;
    setSpeechError('');
    setIsSpeechStarting(true);
    manualSpeechStopRef.current = false;
    speechErrorRef.current = false;
    receivedSpeechRef.current = false;
    finalTranscriptRef.current = '';
    speechBaseTextRef.current = messageText;

    try {
      // Request microphone access explicitly so permission failures can be
      // reported consistently before starting browser speech recognition.
      if (navigator.mediaDevices?.getUserMedia) {
        const permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        permissionStream.getTracks().forEach(track => track.stop());
      }

      if (speechStartRequestRef.current !== requestId) return;

      if (recognitionRef.current) {
        recognitionRef.current.onend = null;
        recognitionRef.current.abort();
        recognitionRef.current = null;
      }

      const recognition = new SpeechRecognition();
      recognition.lang = getSpeechLanguage(speechLanguage);
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        setIsSpeechStarting(false);
        setIsListening(true);
        scheduleSilenceStop();
      };

      recognition.onspeechstart = clearSilenceTimeout;
      recognition.onspeechend = scheduleSilenceStop;

      recognition.onresult = (event) => {
        let interimTranscript = '';

        for (let index = event.resultIndex; index < event.results.length; index += 1) {
          const transcript = event.results[index][0]?.transcript || '';
          if (event.results[index].isFinal) {
            finalTranscriptRef.current += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        const spokenText = `${finalTranscriptRef.current}${interimTranscript}`.trim();
        if (!spokenText) return;

        receivedSpeechRef.current = true;
        scheduleSilenceStop();

        const baseText = speechBaseTextRef.current.trimEnd();
        setText(baseText ? `${baseText} ${spokenText}` : spokenText);
        handleTyping();
      };

      recognition.onerror = (event) => {
        if (event.error === 'aborted' && manualSpeechStopRef.current) return;

        speechErrorRef.current = true;
        const errorMessages = {
          'not-allowed': 'Microphone permission denied. Please allow microphone access.',
          'service-not-allowed': 'Microphone permission denied. Please allow microphone access.',
          'no-speech': 'No speech detected. Please try again.',
          'audio-capture': 'No microphone was found or it is unavailable.',
          network: 'Speech recognition network error. Please try again.',
          'language-not-supported': 'The selected speech language is not supported.',
        };
        setSpeechError(errorMessages[event.error] || 'Speech recognition failed. Please try again.');
      };

      recognition.onend = () => {
        if (recognitionRef.current !== recognition) return;

        clearSilenceTimeout();
        setIsSpeechStarting(false);
        setIsListening(false);
        recognitionRef.current = null;

        if (
          !manualSpeechStopRef.current &&
          !receivedSpeechRef.current &&
          !speechErrorRef.current
        ) {
          setSpeechError('No speech detected. Please try again.');
        }
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (error) {
      if (speechStartRequestRef.current !== requestId) return;

      setIsSpeechStarting(false);
      setIsListening(false);
      if (error?.name === 'NotAllowedError' || error?.name === 'SecurityError') {
        setSpeechError('Microphone permission denied. Please allow microphone access.');
      } else if (error?.name === 'NotFoundError') {
        setSpeechError('No microphone was found or it is unavailable.');
      } else {
        setSpeechError('Unable to start speech recognition. Please try again.');
      }
    }
  };

  const handleSpeechButtonClick = () => {
    if (isListening || isSpeechStarting) {
      speechStartRequestRef.current += 1;
      stopSpeechRecognition();
      setIsSpeechStarting(false);
      return;
    }

    startSpeechRecognition();
  };

  useEffect(() => {
    return () => {
      speechStartRequestRef.current += 1;
      clearSilenceTimeout();
      if (recognitionRef.current) {
        recognitionRef.current.onend = null;
        recognitionRef.current.abort();
        recognitionRef.current = null;
      }
    };
  }, [clearSilenceTimeout]);

  useEffect(() => {
    if (isDisabled) {
      speechStartRequestRef.current += 1;
      stopSpeechRecognition();
      setIsSpeechStarting(false);
    }
  }, [isDisabled, stopSpeechRecognition]);

  // Handle click outside to close menus
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setShowAttachmentMenu(false);
        setShowEmojiPicker(false);
      }
    };

    // Add event listener when menus are open
    if (showAttachmentMenu || showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAttachmentMenu, showEmojiPicker]);

  return (
    <div className={`input-area ${isDisabled ? 'disabled' : ''}`} ref={inputRef}>
      {hasReplyTo && (
        <div className="reply-preview">
          <span className="reply-author">
            {replyPreview.from === 'me' ? 'You' : replyPreview.from}
          </span>
          <span className="reply-text">{replyPreviewText}</span>
          {hasReplyClose && (
            <button className="reply-close" onClick={onReplyClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          )}
        </div>
      )}
      
      {/* Block Message */}
      {isDisabled && hasBlockMessage && (
        <div className="block-message">
          <span>{blockMessageText}</span>
        </div>
      )}
      
      <div className={`input-container ${messageText.trim() && hasSend ? 'has-send' : ''}`}>
        <button 
          className="emoji-btn" 
          onClick={handleEmojiClick}
          aria-label="Emoji"
          disabled={isDisabled}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
            <line x1="9" y1="9" x2="9.01" y2="9"/>
            <line x1="15" y1="9" x2="15.01" y2="9"/>
          </svg>
        </button>

        <button
          type="button"
          className={`speech-to-text-btn ${isListening ? 'listening' : ''}`}
          onClick={handleSpeechButtonClick}
          aria-label={isListening ? 'Stop voice typing' : 'Start voice typing'}
          aria-pressed={isListening}
          title={`${isListening ? 'Stop' : 'Start'} voice typing (${getSpeechLanguage(speechLanguage)})`}
          disabled={isDisabled || !hasSetText}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
            <path d="M19 10v1a7 7 0 0 1-14 0v-1"/>
            <line x1="12" y1="18" x2="12" y2="22"/>
            <line x1="9" y1="22" x2="15" y2="22"/>
          </svg>
        </button>
        
        <button 
          className={`attachment-btn ${showAttachmentMenu ? 'active' : ''}`}
          onClick={handleAttachmentClick}
          aria-label="Attachment"
          disabled={isDisabled}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66L9.64 16.2a2 2 0 01-2.83-2.83l8.49-8.49"/>
          </svg>
        </button>
        
        <input
          value={messageText}
          onChange={e => {
            if (hasSetText) {
              setText(e.target.value);
              // Trigger typing indicator when user types
              if (e.target.value.trim() && !isDisabled) {
                handleTyping();
              } else if (!e.target.value.trim()) {
                stopTyping();
              }
            }
          }}
          placeholder={isDisabled ? (hasBlockMessage ? blockMessageText : "Cannot send messages") : "Type a message..."}
          onKeyDown={e => {
            if (e.key === 'Enter' && messageText.trim() && !isDisabled && hasSend) {
              stopTyping();
              handleSend();
            }
          }}
          onBlur={() => {
            // Stop typing when input loses focus
            setTimeout(stopTyping, 1000);
          }}
          className="message-input"
          disabled={isDisabled}
        />
        
        {messageText.trim() && hasSend ? (
          <button 
            className="send-btn" 
            onClick={handleSend}
            aria-label="Send"
            disabled={isDisabled}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22,2 15,22 11,13 2,9 22,2"/>
            </svg>
          </button>
        ) : (
          hasVoiceMessage && (
            <VoiceRecorder 
              onVoiceMessage={onVoiceMessage}
              disabled={isDisabled}
            />
          )
        )}
      </div>

      {(isSpeechStarting || isListening || speechError) && (
        <div
          className={`speech-status ${speechError ? 'error' : ''}`}
          role="status"
          aria-live="polite"
        >
          {!speechError && (
            <span className={`speech-status-dot ${isListening ? 'listening' : ''}`} />
          )}
          <span>
            {speechError || (isListening ? 'Listening...' : 'Starting microphone...')}
          </span>
        </div>
      )}

      {/* Attachment Menu */}
      {showAttachmentMenu && (
        <AttachmentMenu 
          onOptionClick={handleAttachmentOption}
          onClose={closeAttachmentMenu}
        />
      )}

      {/* Emoji Picker */}
      {showEmojiPicker && (
        <EmojiPicker 
          onEmojiSelect={handleEmojiSelect}
          onGifSelect={handleGifSelect}
          onClose={closeEmojiPicker}
        />
      )}

      {/* Media Picker */}
      {hasMediaSend && (
        <MediaPicker 
          isVisible={showMediaPicker}
          onMediaSelect={handleMediaSelect}
          onClose={closeMediaPicker}
        />
      )}

      {/* Camera Capture */}
      {hasMediaSend && (
        <CameraCapture 
          isVisible={showCameraCapture}
          onCapture={handleCameraCapture}
          onClose={closeCameraCapture}
        />
      )}

      {/* Document Picker */}
      {hasDocumentSend && (
        <DocumentPicker 
          isVisible={showDocumentPicker}
          onDocumentSelect={handleDocumentSelect}
          onClose={closeDocumentPicker}
          isUploading={isDocumentUploading}
        />
      )}

      {/* Audio Picker */}
      {hasAudioSend && (
        <AudioPicker 
          isVisible={showAudioPicker}
          onAudioSelect={handleAudioSelect}
          onClose={closeAudioPicker}
          isUploading={isAudioUploading}
        />
      )}

      {/* Contact Picker */}
      {hasContactSend && (
        <ContactPicker 
          isVisible={showContactPicker}
          onContactSelect={handleContactSelect}
          onClose={closeContactPicker}
          user={user}
        />
      )}

      {/* Location Picker */}
      {hasLocationSend && (
        <LocationPicker 
          isVisible={showLocationPicker}
          onLocationSelect={handleLocationSelect}
          onClose={closeLocationPicker}
        />
      )}
    </div>
  );
}

export default MessageInput; 